import type { DashboardFilters } from "@home-finance-manager/shared/types/dashboard"
import type { ExpenseFilters } from "@home-finance-manager/shared/types/expenses"

/**
 * Serialize DashboardFilters to URL search params
 */
export function serializeDashboardFilters(filters: DashboardFilters): Record<string, string> {
  const params: Record<string, string> = {}

  if (filters.search) {
    params.search = filters.search
  }

  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.categoryIds = filters.categoryIds.join(",")
  }

  if (filters.partnerIds && filters.partnerIds.length > 0) {
    params.partnerIds = filters.partnerIds.join(",")
  }

  if (filters.startDate) {
    params.startDate = filters.startDate
  }

  if (filters.endDate) {
    params.endDate = filters.endDate
  }

  if (filters.includeVirtualExpenses === true) {
    params.includeVirtualExpenses = "true"
  }

  return params
}

/**
 * Deserialize URL search params to DashboardFilters
 */
export function deserializeDashboardFilters(params: URLSearchParams): DashboardFilters {
  const filters: DashboardFilters = {}

  const search = params.get("search")
  if (search) {
    filters.search = search
  }

  const categoryIds = params.get("categoryIds")
  if (categoryIds) {
    filters.categoryIds = categoryIds
      .split(",")
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id))
  }

  const partnerIds = params.get("partnerIds")
  if (partnerIds) {
    filters.partnerIds = partnerIds
      .split(",")
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id))
  }

  const startDate = params.get("startDate")
  if (startDate) {
    filters.startDate = startDate
  }

  const endDate = params.get("endDate")
  if (endDate) {
    filters.endDate = endDate
  }

  const includeVirtualExpenses = params.get("includeVirtualExpenses")
  if (includeVirtualExpenses === "true") {
    filters.includeVirtualExpenses = true
  }

  return filters
}

/**
 * Serialize ExpenseFilters to URL search params
 */
export function serializeExpenseFilters(filters: ExpenseFilters): Record<string, string> {
  const params: Record<string, string> = {}

  if (filters.search) {
    params.search = filters.search
  }

  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.categoryIds = filters.categoryIds.join(",")
  }

  if (filters.recurringTypes && filters.recurringTypes.length > 0) {
    params.recurringTypes = filters.recurringTypes.join(",")
  }

  if (filters.isVirtual !== undefined) {
    params.isVirtual = filters.isVirtual
  }

  if (filters.includeSplits) {
    params.includeSplits = String(filters.includeSplits)
  }

  return params
}

/**
 * Deserialize URL search params to ExpenseFilters
 */
export function deserializeExpenseFilters(params: URLSearchParams): ExpenseFilters {
  const filters: ExpenseFilters = {}

  const search = params.get("search")
  if (search) {
    filters.search = search
  }

  const categoryIds = params.get("categoryIds")
  if (categoryIds) {
    filters.categoryIds = categoryIds
      .split(",")
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id))
  }

  const recurringTypes = params.get("recurringTypes")
  if (recurringTypes) {
    filters.recurringTypes = recurringTypes.split(",").filter(Boolean)
  }

  const isVirtual = params.get("isVirtual")
  if (isVirtual !== null) {
    filters.isVirtual = isVirtual
  }

  const includeSplits = params.get("includeSplits")
  if (includeSplits) {
    filters.includeSplits = includeSplits === "true"
  }

  return filters
}
